'use client'

import type { MouseEventHandler } from 'react'

const PREFERRED_SOURCE_URL =
  'https://www.google.com/preferences/source?q=microplasticsapp.com'

export default function PreferredSourceButton({
  onClick,
  className = '',
  compact = false,
}: {
  onClick?: MouseEventHandler<HTMLAnchorElement>
  className?: string
  compact?: boolean
}) {
  const sizing = compact
    ? 'min-h-[40px] gap-2 rounded-xl px-3 py-2'
    : 'min-h-[48px] gap-3 rounded-2xl px-4 py-2.5'

  return (
    <a
      href={PREFERRED_SOURCE_URL}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      className={`group relative inline-flex max-w-full items-center overflow-hidden border border-gray-500 bg-white text-left text-gray-950 shadow-[0_0_10px_2px_rgba(44,122,86,0.28),0_0_24px_6px_rgba(70,180,126,0.14)] transition duration-300 ease-out hover:-translate-y-0.5 hover:scale-[1.02] hover:border-forest hover:shadow-[0_0_14px_3px_rgba(44,122,86,0.42),0_0_32px_8px_rgba(70,180,126,0.22)] focus:outline-none focus-visible:ring-2 focus-visible:ring-forest focus-visible:ring-offset-2 motion-reduce:hover:translate-y-0 motion-reduce:hover:scale-100 ${sizing} ${className}`}
      aria-label="Add MicroPlastics as a preferred source on Google"
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 -left-1/2 w-1/3 -skew-x-12 bg-gradient-to-r from-transparent via-forest/10 to-transparent opacity-0 transition-all duration-500 group-hover:left-[120%] group-hover:opacity-100"
      />
      <svg
        aria-hidden="true"
        viewBox="0 0 18 18"
        className={`${compact ? 'h-5 w-5' : 'h-7 w-7'} relative shrink-0 transition-transform duration-300 group-hover:rotate-6 group-hover:scale-110 motion-reduce:group-hover:rotate-0 motion-reduce:group-hover:scale-100`}
      >
        <path
          fill="#4285F4"
          d="M17.64 9.205c0-.638-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.797 2.716v2.258h2.909c1.702-1.567 2.684-3.875 2.684-6.614Z"
        />
        <path
          fill="#34A853"
          d="M9 18c2.43 0 4.468-.806 5.956-2.181l-2.909-2.258c-.806.54-1.835.859-3.047.859-2.344 0-4.328-1.585-5.037-3.714H.956v2.332A9 9 0 0 0 9 18Z"
        />
        <path
          fill="#FBBC05"
          d="M3.963 10.706A5.41 5.41 0 0 1 3.682 9c0-.592.102-1.167.281-1.706V4.962H.956A9 9 0 0 0 0 9c0 1.452.347 2.827.956 4.038l3.007-2.332Z"
        />
        <path
          fill="#EA4335"
          d="M9 3.58c1.321 0 2.507.454 3.44 1.345l2.581-2.581C13.464.892 11.426 0 9 0A9 9 0 0 0 .956 4.962l3.007 2.332C4.672 5.165 6.656 3.58 9 3.58Z"
        />
      </svg>
      <span
        className={`${compact ? 'text-xs leading-none' : 'text-[15px] leading-[1.15] sm:text-base'} relative font-medium tracking-[-0.01em]`}
      >
        {compact ? (
          'Add us on Google'
        ) : (
          <>
            Add as a preferred
            <br />
            source on Google
          </>
        )}
      </span>
    </a>
  )
}
